import CodeDesc from '../../code-desc';
import { array, fn, get, hash } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import ensureSafeComponent from '@embroider/util/_app_/helpers/ensure-safe-component.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import queue from '@nullvoxpopuli/ember-composable-helpers/helpers/queue';
import { restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import t from 'ember-intl/helpers/t';
import isSame from 'ember-moment/helpers/is-same';
import now from 'ember-moment/helpers/now';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import lowerCase from 'lodash/lowerCase';
import moment from 'moment-timezone';
import BvBadge from 'volta/components/bv-badge';
import BvBtn from 'volta/components/bv-btn';
import BvBtnGroup from 'volta/components/bv-btn-group';
import BvCalendar from 'volta/components/bv-calendar';
import BvCard from 'volta/components/bv-card';
import BvHr from 'volta/components/bv-hr';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvLabelled from 'volta/components/bv-labelled';
import BvPopover from 'volta/components/bv-popover';
import BvScrollLock from 'volta/components/bv-scroll-lock';
import BvText from 'volta/components/bv-text';
import SupplierSelect from 'volta/components/supplier-select';
import WarehouseSelect from 'volta/components/warehouse-select';
import workshopSelect from 'volta/components/workshop-select';
import cannot from 'volta/helpers/cannot';
import classNames from 'volta/helpers/class-names';
import { SupplierResourceName } from 'volta/models/supplier';
import SupplierCalendarDay, {
  SupplierCalendarDayResourceName
} from 'volta/models/supplier-calendar-day';
import { WarehouseResourceName } from 'volta/models/warehouse';
import WarehouseCalendarDay, {
  WarehouseCalendarDayResourceName
} from 'volta/models/warehouse-calendar-day';
import WorkshopCalendarDay, {
  WorkshopCalendarDayResourceName
} from 'volta/models/workshop-calendar-day';
import { indexBy } from 'volta/utils/array-utils';
import { formatISODate, formatLocalDate, isWeekendDay } from 'volta/utils/date-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import type { ComponentLike } from '@glint/template';
import type { IntlService } from 'ember-intl';
import type { BvCardSectionSignature } from 'volta/components/bv-card/section';
import type { IResourceName } from 'volta/models/base-model';
import type {
  IIndexedCalendarDay,
  TCalendarDay,
  TDate,
  TEntityCalendarDay,
  TEntityIdKey,
  TOpeningKeys,
  TResetDayPayload,
  TUpdateDayPayload
} from 'volta/models/types/calendar';
import type AppSettings from 'volta/services/app-settings';
import type AuthorizationService from 'volta/services/authorization';
import type BvFlashService from 'volta/services/bv-flash';
import type CalendarService from 'volta/services/calendar-service';
import type { TError } from 'volta/utils/error-utils';

const TODAY = moment.utc().toDate();

interface ICalendarConfig {
  label: string;
  entityId: TEntityIdKey;
  Model: typeof WarehouseCalendarDay | typeof SupplierCalendarDay | typeof WorkshopCalendarDay;
  Selector: typeof WarehouseSelect | typeof SupplierSelect | typeof workshopSelect;
  permission: string;
  parentEntityName: IResourceName;
  payloadKeys: TOpeningKeys[];
}

export const CALENDAR_CONFIG_BY_RESOURCENAME = {
  [WarehouseCalendarDayResourceName.singular]: {
    label: 'warehouse',
    entityId: 'warehouseId',
    parentEntityName: WarehouseResourceName,
    Model: WarehouseCalendarDay,
    Selector: WarehouseSelect,
    permission: 'UpdateWarehouse',
    payloadKeys: ['openForReception', 'openForDemand', 'openForProduction']
  },
  [SupplierCalendarDayResourceName.singular]: {
    label: 'supplier',
    entityId: 'supplierId',
    parentEntityName: SupplierResourceName,
    Model: SupplierCalendarDay,
    Selector: SupplierSelect,
    permission: 'UpdateSupplier',
    payloadKeys: ['openForReception']
  },
  [WorkshopCalendarDayResourceName.singular]: {
    label: 'workshop',
    entityId: 'workshopId',
    parentEntityName: WorkshopCalendarDayResourceName,
    Model: WorkshopCalendarDay,
    Selector: workshopSelect,
    permission: 'UpdateWorkshop',
    payloadKeys: ['openForReception', 'openForDemand']
  }
} as Record<string, ICalendarConfig>;

type PowerCalendarDateRange<T = Date> = { start: T; end: T | null };

interface FormsCalendarEditorSignature {
  Args: {
    entityId: string;
    resourceName: IResourceName;
    withReception?: boolean;
    withDemand?: boolean;
    closedWeekDays?: number[];
    onSuccess?: (result: DoneResult) => void;
  };
  Blocks: {
    calendarDay: [{ busyDay: TCalendarDay; date: Date }];
    calendarHeader: [{ Section: ComponentLike<BvCardSectionSignature> }];
  };
  Element: HTMLElement;
}

export default class CalendarEditor extends Component<FormsCalendarEditorSignature> {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    <BvCard class='calendar-editor' ...attributes as |card|>
      <card.Header @title={{t 'calendar'}}>
        <BvPopover
          @horizontalPosition='right'
          @preventScroll={{false}}
          @size='md'
          class='u-d-flex u-justify-content-end'
          as |popover|
        >
          {{#if popover.isOpen}}
            <BvScrollLock @selector='.bv-app__content' />
          {{/if}}
          <popover.Trigger class='u-d-flex u-justify-content-end'>
            <BvBtn
              @text={{t 'calendarEditor.copy'}}
              @size='sm'
              @subtle={{true}}
              @disabled={{this.notAllowed}}
            />
          </popover.Trigger>
          <popover.Content class='u-ml-5'>
            <popover.Body @scrollable={{true}}>
              <div class='u-mt-2'>
                <BvLabelled @label={{t this.calendarConfig.label}}>
                  {{#let
                    (component (ensureSafeComponent (get this.calendarConfig 'Selector') this))
                    as |Select|
                  }}
                    <Select
                      @multiple={{false}}
                      @value={{this.importedCalendar}}
                      @preventScroll={{false}}
                      @isLoading={{this.importCalendar.isRunning}}
                      @excludedOptions={{array @entityId}}
                      @dataName={{this.calendarConfig.label}}
                      @onChange={{fn (queue popover.close (perform this.importCalendar))}}
                      as |item|
                    >
                      <CodeDesc @code={{item.code}} @desc={{item.description}} @color='success' />
                    </Select>
                  {{/let}}
                </BvLabelled>
              </div>
            </popover.Body>
            <popover.Footer class='u-justify-content-between'>
              <BvBtn @text={{t 'close'}} @onClick={{popover.close}} />
            </popover.Footer>
          </popover.Content>
        </BvPopover>
      </card.Header>
      {{#if (has-block 'calendarHeader')}}
        {{yield (hash Section=card.Section) to='calendarHeader'}}
      {{/if}}
      <card.Section>
        {{! template-lint-disable no-at-ember-render-modifiers }}
        <BvCalendar
          @center={{this.calCenter}}
          @onCenterChange={{this.handleCenterChange}}
          @selected={{this.selectedRange}}
          @onSelect={{this.onCalendarSelect}}
          @withRange={{true}}
          @minRange={{0}}
          @size='auto'
          @minDate={{this.minDate}}
          @disabled={{this.notAllowed}}
          class='calendar u-mx-auto'
          {{didInsert (perform this.initCalendarDays)}}
          {{didUpdate (perform this.initCalendarDays) this.calYear}}
          as |calendar|
        >
          <calendar.Nav />
          <calendar.Days as |day|>
            {{#let (this.findCalendarDate day.date) as |busyDay|}}
              {{#if (has-block 'calendarDay')}}
                {{yield (hash busyDay=busyDay date=day.date) to='calendarDay'}}
              {{/if}}
              <BvText @text={{day.number}} class='calendar-day' />
              {{#if busyDay}}
                <div class='closed-day-bars'>
                  {{#if @withDemand}}
                    <div
                      class={{if
                        busyDay.openForDemand
                        'closed-day-bar closed-day-bar--hidden'
                        (if
                          (and busyDay.isDefault (this.isAWeekendDay day.date))
                          'closed-day-bar closed-day-bar--demand--we'
                          'closed-day-bar closed-day-bar--demand'
                        )
                      }}
                    ></div>
                  {{/if}}
                  {{#if @withReception}}
                    <div
                      class={{if
                        busyDay.openForReception
                        'closed-day-bar closed-day-bar--hidden'
                        (if
                          (and busyDay.isDefault (this.isAWeekendDay day.date))
                          'closed-day-bar closed-day-bar--reception--we'
                          'closed-day-bar closed-day-bar--reception'
                        )
                      }}
                    ></div>
                  {{/if}}
                </div>
              {{/if}}
            {{/let}}
          </calendar.Days>
        </BvCalendar>
        <BvBadge
          @text={{t (if @closedWeekDays 'closed' 'weekend')}}
          @outline={{true}}
          class={{classNames 'u-mb-1 u-mt-2' (unless this.showWeekendTag 'u-opacity-none')}}
        />
        <BvInline @blockAlign='center' @align='space-between'>
          <BvInline @blockAlign='center'>
            <BvText @variant='heading-md' @text={{this.selectedRangeTitle}} />
            <BvBtn
              @text={{t 'reset'}}
              @subtle={{true}}
              @size='sm'
              @onClick={{perform this.resetDays}}
              @disabled={{or
                (cannot 'ResetOne' @resourceName.plural)
                (cannot 'ResetDays' @resourceName.plural)
                this.notAllowed
              }}
              @testName='calendar-reset'
            />
          </BvInline>
          <BvBtn
            @text={{t 'today'}}
            @size='sm'
            @onClick={{this.resetCenter}}
            @pressed={{or
              (isSame this.selectedRange.start (now) precision='day')
              (not this.selectedRange)
            }}
            @testName='calendar-today'
          />
        </BvInline>
        <BvText
          @as='p'
          @color='subdued'
          @text={{t 'calendarEditor.hint' entity=(lowerCase (t this.calendarConfig.label))}}
          class='u-mb-2'
        />
        {{#let
          this.calendarOpeningStates.openForDemand this.calendarOpeningStates.openForReception
          as |openForDemand openForReception|
        }}
          {{#if @withDemand}}
            <BvInline @align='space-between' @blockAlign='center'>
              <BvInline @blockAlign='center' class='u-py-1'>
                <BvInline @blockAlign='center' class='u-mr-2'>
                  <BvIcon
                    @color='blue'
                    @hasBackdrop={{true}}
                    @glyph={{if openForDemand 'lock-open' 'lock-closed'}}
                  />
                </BvInline>
                <BvText @text={{t 'calendarEditor.openedForDemand'}} />
              </BvInline>
              <BvBtnGroup @color='primary' @outline={{true}} @size='sm' as |g|>
                <g.btn
                  @onClick={{perform this.handleOpeningChange 'openForDemand' true}}
                  @active={{openForDemand}}
                  @disabled={{or
                    (cannot 'UpdateCalendarDays' @resourceName.plural)
                    this.notAllowed
                  }}
                  @text={{t 'yes'}}
                  @testName='open-demand-yes'
                />
                <g.btn
                  @onClick={{perform this.handleOpeningChange 'openForDemand' false}}
                  @active={{not openForDemand}}
                  @disabled={{or
                    (cannot 'UpdateCalendarDays' @resourceName.plural)
                    this.notAllowed
                  }}
                  @text={{t 'no'}}
                  @testName='open-demand-no'
                />
              </BvBtnGroup>
            </BvInline>
            <BvHr class='u-my-2' />
          {{/if}}
          {{#if @withReception}}
            <BvInline @align='space-between' @blockAlign='center'>
              <BvInline @blockAlign='center' class='u-py-1'>
                <BvInline @blockAlign='center' class='u-mr-2'>
                  <BvIcon
                    @color='orange'
                    @hasBackdrop={{true}}
                    @glyph={{if openForReception 'lock-open' 'lock-closed'}}
                  />
                </BvInline>
                <BvText @text={{t 'calendarEditor.openedForReception'}} />
              </BvInline>
              <BvBtnGroup @color='primary' @outline={{true}} @size='sm' as |g|>
                <g.btn
                  @onClick={{perform this.handleOpeningChange 'openForReception' true}}
                  @active={{openForReception}}
                  @text={{t 'yes'}}
                  @disabled={{or
                    (cannot 'UpdateCalendarDays' @resourceName.plural)
                    this.notAllowed
                  }}
                  @testName='open-reception-yes'
                />
                <g.btn
                  @onClick={{perform this.handleOpeningChange 'openForReception' false}}
                  @active={{not openForReception}}
                  @text={{t 'no'}}
                  @disabled={{or
                    (cannot 'UpdateCalendarDays' @resourceName.plural)
                    this.notAllowed
                  }}
                  @testName='open-reception-no'
                />
              </BvBtnGroup>
            </BvInline>
          {{/if}}
        {{/let}}
      </card.Section>
    </BvCard>
  </template>
  @service calendarService!: CalendarService;
  @service appSettings!: AppSettings;
  @service authorization!: AuthorizationService;

  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;

  @tracked calCenter = TODAY;
  @tracked selectedRange?: PowerCalendarDateRange = { start: TODAY, end: TODAY };

  @tracked importedCalendar?: string;

  minDate = moment.utc().subtract(1, 'year').toDate();

  get calYear(): number {
    return moment.utc(this.calCenter).year();
  }

  get calendarConfig() {
    return CALENDAR_CONFIG_BY_RESOURCENAME[this.args.resourceName.singular];
  }

  get notAllowed() {
    const { resourceName } = this.args;
    return this.calendarConfig
      ? this.authorization.cannot(resourceName.plural, this.calendarConfig?.permission)
      : true;
  }

  get calendarDays() {
    return (
      (this.calendarService.cache[this.importedCalendar ?? this.args.entityId] ?? {})[
        this.calYear
      ] ?? []
    );
  }

  get weekendDays() {
    if (this.args.resourceName.singular === WarehouseCalendarDayResourceName.singular) {
      return this.appSettings.byWarehouseOrDefault(this.args.entityId)?.settings.weekendDays;
    }
    return this.args.closedWeekDays;
  }

  get indexedCalendarDays(): Record<string, IIndexedCalendarDay> {
    if (!this.calendarDays?.length) {
      return {};
    }

    return indexBy<IIndexedCalendarDay>(
      this.calendarDays.map(
        (calD) =>
          ({
            ...calD.toJSON(),
            date: formatISODate(calD.date)
          }) as IIndexedCalendarDay
      ),
      'date'
    );
  }

  get showWeekendTag() {
    const { selectedRange } = this;

    if (selectedRange) {
      const { start, end } = selectedRange;

      if (start && !end) {
        return this.isAWeekendDay(start);
      }

      if (start && end) {
        const days: boolean[] = [];
        this.whileBrowsingSelectedDays((day) => {
          days.push(this.isAWeekendDay(day));
        });
        return days.every(Boolean);
      }
    }

    return this.isAWeekendDay(this.calCenter);
  }

  get selectedRangeTitle() {
    if (this.selectedRange) {
      let title = '';
      if (this.selectedRange.start) {
        title += formatLocalDate(this.selectedRange.start);
      }
      if (this.selectedRange.end) {
        title += ' — ' + formatLocalDate(this.selectedRange.end);
      }
      return title;
    } else {
      return formatLocalDate();
    }
  }

  get calendarOpeningStates() {
    const { selectedRange, indexedCalendarDays } = this;

    if (!selectedRange) {
      const calendarDay = indexedCalendarDays[formatISODate()];
      return calendarDay ?? this.defaultState(moment.utc().toDate());
    }

    const { start, end } = selectedRange;

    if (start && !end) {
      const calendarDay = indexedCalendarDays[formatISODate(start)];
      return calendarDay ?? this.defaultState(start);
    }

    if (start && end) {
      const state: { openForDemand: boolean[]; openForReception: boolean[] } = {
        openForDemand: [],
        openForReception: []
      };

      this.whileBrowsingSelectedDays((day) => {
        const calendarDay = indexedCalendarDays[formatISODate(day)];
        (Object.keys(state) as (keyof typeof state)[]).forEach((key: keyof typeof state) => {
          if (calendarDay) {
            state[key].push(calendarDay[key]);
          } else {
            state[key].push(!this.isAWeekendDay(day));
          }
        });
      });

      const isOpened = (key: keyof typeof state) =>
        state[key].every(Boolean) || (state[key].every((d) => !d) ? false : 'indeterminate');

      return {
        openForDemand: isOpened('openForDemand'),
        openForReception: isOpened('openForReception')
      };
    }
    return this.defaultState(new Date());
  }

  @action
  onCalendarSelect(days: {
    date: PowerCalendarDateRange;
    moment: PowerCalendarDateRange<moment.Moment>;
  }) {
    this.selectedRange = days.moment.start.isSame(days.moment.end, 'days')
      ? { start: days.date.start, end: null }
      : days.date;
  }

  @action
  handleCenterChange(center: { date: Date }) {
    this.calCenter = center.date;
  }

  @action
  resetCenter() {
    this.calCenter = TODAY;
    this.selectedRange = { start: TODAY, end: TODAY };
  }

  private whileBrowsingSelectedDays(fn: (day: moment.Moment) => void) {
    const { start, end } = this.selectedRange ?? {};
    if (!start || !end) {
      return;
    }

    const day = moment.utc(start);

    while (!day.isAfter(moment.utc(end), 'days')) {
      fn(day);
      day.add(1, 'day');
    }
  }

  importCalendar = restartableTask(async (entityId: string) => {
    const { resourceName } = this.args;
    const entityIdKey = this.calendarConfig?.entityId;
    if (!entityIdKey) {
      return;
    }
    try {
      this.importedCalendar = entityId;

      await this.calendarService.fetchCalendarDays.perform(resourceName, entityId, this.calYear);
      const currentCalDays = this.calendarDays;
      if (currentCalDays.length > 0) {
        const payload = {
          [entityIdKey]: this.args.entityId,
          dates: currentCalDays.map((calDay) => formatISODate(calDay.date))
        } as TResetDayPayload;

        await this.resetCalendarDays.perform(payload);
      }

      const importedDays = this.calendarDays;
      if (importedDays.length === 0) {
        return;
      }
      const days = importedDays.reduce((acc, day) => {
        return { ...acc, ...day.indexedDay };
      }, {});

      await this.updateCalendar.perform({
        [entityIdKey]: this.args.entityId,
        days
      } as TUpdateDayPayload);
      this.bvFlash.success(this.intl.t('calendarEditor.copySuccess'));
    } catch (error) {
      defaultErrorHandler(error as TError);
      this.bvFlash.error(this.intl.t('calendarEditor.copyError'));
    }
  });

  initCalendarDays = restartableTask(async () => {
    const { entityId, resourceName } = this.args;
    return (
      (await this.calendarService.fetchCalendarDays.perform(
        resourceName,
        entityId,
        this.calYear
      )) ?? []
    );
  });

  handleOpeningChange = restartableTask(async (key: TOpeningKeys, checked: boolean) => {
    const { selectedRange } = this;
    this.unselectImportedCalendar();
    if (selectedRange) {
      const { start, end } = selectedRange;

      if (start && !end) {
        return this.updateForADay.perform(start, key, checked);
      }
      const entityId = this.calendarConfig?.entityId;

      if (start && end && entityId) {
        const payload: Partial<TUpdateDayPayload> = {
          [entityId]: this.args.entityId,
          days: {}
        };

        let updatedDays = {};
        this.whileBrowsingSelectedDays((day) => {
          const updatedDay = this.generatePayloadForADay(day, key, checked);
          updatedDays = { ...updatedDays, ...updatedDay };
        });
        payload.days = { ...updatedDays };
        await this.updateCalendar.perform(payload as TUpdateDayPayload);

        return this.calendarDays;
      }
    } else {
      return this.updateForADay.perform(undefined, key, checked);
    }

    return;
  });

  resetDays = restartableTask(async () => {
    const { selectedRange } = this;
    this.unselectImportedCalendar();

    if (!selectedRange) {
      return this.removeADay.perform();
    }

    const { start, end } = selectedRange;

    if (start && !end) {
      return this.removeADay.perform(start);
    }

    const { entityId } = this.calendarConfig ?? {};

    if (start && end && entityId) {
      const payload = {
        [entityId]: this.args.entityId,
        dates: [] as string[]
      } as TResetDayPayload;

      const daysToReset: string[] = [];
      this.whileBrowsingSelectedDays((day) => {
        daysToReset.push(formatISODate(day));
      });
      payload.dates = daysToReset;

      await this.resetCalendarDays.perform(payload);
      return this.calendarDays;
    }

    return;
  });

  updateForADay = restartableTask(
    async (day: TDate | undefined, key: TOpeningKeys, checked: boolean) => {
      this.unselectImportedCalendar();
      const { entityId } = this.calendarConfig ?? {};
      if (entityId) {
        const payload = {
          [entityId]: this.args.entityId,
          days: {
            ...this.generatePayloadForADay(moment.utc(day), key, checked)
          }
        } as TUpdateDayPayload;

        await this.updateCalendar.perform(payload);

        return this.calendarDays;
      }
      return;
    }
  );

  updateCalendar = restartableTask(async (payload: TUpdateDayPayload) => {
    const { Model } = this.calendarConfig ?? {};
    if (!Model) {
      return;
    }
    try {
      const result: WarehouseCalendarDay[] | SupplierCalendarDay[] | WorkshopCalendarDay[] =
        // @ts-ignore Polymorphic class
        await Model.updateCalendar(payload);
      this.args.onSuccess?.(result);
      return result;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  removeADay = restartableTask(async (day?: TDate) => {
    this.unselectImportedCalendar();

    const dayToRemove = this.calendarDays.find((c) => formatISODate(c.date) === formatISODate(day));
    if (dayToRemove) {
      await this.resetCalendarDay.perform(dayToRemove);
    }
  });

  resetCalendarDay = restartableTask(async (calendarDay: TCalendarDay) => {
    try {
      // @ts-expect-error Polymorphic class
      const result = await calendarDay.resetOne(undefined);
      this.args.onSuccess?.(result);
      return result;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  resetCalendarDays = restartableTask(async (payload: TResetDayPayload) => {
    try {
      const { Model } = this.calendarConfig ?? {};
      if (!Model) {
        return;
      }

      const result = await (Model as typeof WarehouseCalendarDay).resetDays(payload);
      this.args.onSuccess?.(result);
      return result;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  });

  private defaultState(date: TDate) {
    return this.calendarService.buildDefaultState(date, this.weekendDays);
  }

  private generatePayloadForADay(
    day: moment.Moment | Date,
    key: TOpeningKeys,
    checked: boolean
  ): TEntityCalendarDay {
    const calendarDay = this.indexedCalendarDays[formatISODate(day)];
    const { payloadKeys = [] } = this.calendarConfig ?? {};
    const payload: Partial<Record<TOpeningKeys, boolean>> = {};
    for (const payloadKey of payloadKeys) {
      payload[payloadKey] = calendarDay ? calendarDay[payloadKey] : !this.isAWeekendDay(day);
    }

    payload[key] = checked;

    return { [formatISODate(day)]: payload as Record<TOpeningKeys, boolean> };
  }

  private unselectImportedCalendar() {
    this.importedCalendar = undefined;
  }

  // Template Helpers
  // ~~~~~~

  findCalendarDate: (date: Date) => TCalendarDay = (date: Date) => {
    return this.indexedCalendarDays[formatISODate(date)] ?? this.defaultState(date);
  };

  isAWeekendDay = (date: TDate) => {
    return isWeekendDay(date, this.weekendDays);
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Forms::CalendarEditor': typeof CalendarEditor;
    'forms/calendar-editor': typeof CalendarEditor;
  }
}
